import type { Action } from "@kikik/mahjong-core";
import { useI18n } from "../i18n";
import type { TableModel } from "../game/tableModel";
import { claimActions } from "../game/tableModel";
import { MahjongTile } from "./tiles";

export interface ClaimBarProps {
  model: TableModel;
  onClaim: (action: Action) => void;
  /** The claim the coach recommends, highlighted for a learner. */
  suggested?: Action | null;
}

function sameAction(a: Action, b: Action): boolean {
  if (a.type !== b.type || a.playerId !== b.playerId) return false;
  const aTiles = "tileIds" in a ? a.tileIds.join("-") : "";
  const bTiles = "tileIds" in b ? b.tileIds.join("-") : "";
  return aTiles === bTiles;
}

/**
 * Claim controls, shown only while a discard is actually on offer.
 *
 * It appears right above the rack, where the player's eyes already are, and
 * disappears the moment the window closes. Win is styled apart from the other
 * claims and Pass is always present when the engine offers it, so no one has
 * to guess how to decline.
 */
export function ClaimBar({ model, onClaim, suggested }: ClaimBarProps) {
  const { t, tile, claim } = useI18n();
  if (model.phase !== "awaiting-claims") return null;
  const pending = model.pendingClaims;
  if (!pending) return null;

  const actions = claimActions(model);
  const yourTurnToDecide = pending.waitingOn.includes(model.you);

  return (
    <section
      className={`claims ${yourTurnToDecide ? "claims--active" : ""}`}
      data-testid="claim-bar"
      aria-live="polite"
    >
      <div className="claims__offer">
        <h2 className="claims__title">{t("claims.title")}</h2>
        <span className="claims__tile">
          <MahjongTile kind={pending.discard.tile.kind} size="sm" latest />
          <span>{tile(pending.discard.tile.kind)}</span>
        </span>
      </div>

      {yourTurnToDecide ? (
        <div className="claims__buttons">
          {actions.map((action, index) => {
            const isSuggested = suggested ? sameAction(action, suggested) : false;
            return (
              <button
                key={`${action.type}-${index}`}
                type="button"
                data-action={action.type}
                className={[
                  "button",
                  action.type === "ron"
                    ? "button--win"
                    : action.type === "pass"
                      ? "button--ghost"
                      : "button--claimable",
                  isSuggested ? "button--suggested" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => onClaim(action)}
              >
                {claim(action.type)}
                {action.type === "chow" && "tileIds" in action ? (
                  <span className="claims__hint">
                    {" "}
                    {t("claims.chowHint", { count: action.tileIds.length })}
                  </span>
                ) : null}
                {isSuggested ? (
                  <span className="claims__hint"> {t("claims.suggested")}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : (
        <p className="muted claims__waiting">{t("claims.waitingOthers")}</p>
      )}

      {pending.declared.length > 0 ? (
        <p className="muted claims__declared">
          {t("claims.declared", {
            list: pending.declared.map((declared) => claim(declared.type)).join(", "),
          })}
        </p>
      ) : null}
    </section>
  );
}
